let loadingCursorCount = 0;
let loadingCursorSpinner = null;
let spinnerAnimation = null;
let pointerTrackingInitialized = false;
let lastPointerX = null;
let lastPointerY = null;
const SPINNER_OFFSET_PX = 18;
const POINTER_STORAGE_KEY = 'loadingCursorLastPointer';
const canUseLoadingCursorDom = typeof window !== 'undefined' && typeof document !== 'undefined';
function restoreStoredPointerPosition() {
    if (!canUseLoadingCursorDom || typeof sessionStorage === 'undefined') {
        return;
    }
    try {
        const raw = sessionStorage.getItem(POINTER_STORAGE_KEY);
        if (!raw) {
            return;
        }
        const parsed = JSON.parse(raw);
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
            lastPointerX = parsed.x;
            lastPointerY = parsed.y;
        }
    }
    catch {
        // Ignore storage parsing/access errors
    }
}
function persistPointerPosition() {
    if (!canUseLoadingCursorDom || typeof sessionStorage === 'undefined') {
        return;
    }
    if (lastPointerX === null || lastPointerY === null) {
        return;
    }
    try {
        sessionStorage.setItem(POINTER_STORAGE_KEY, JSON.stringify({ x: lastPointerX, y: lastPointerY }));
    }
    catch {
        // Ignore storage write/access errors
    }
}
function getSpinnerPosition() {
    if (!canUseLoadingCursorDom) {
        return { x: 0, y: 0 };
    }
    const fallbackX = Math.round(window.innerWidth / 2);
    const fallbackY = Math.round(window.innerHeight / 2);
    const x = lastPointerX ?? fallbackX;
    const y = lastPointerY ?? fallbackY;
    return {
        x: Math.max(0, Math.min(window.innerWidth - 1, x + SPINNER_OFFSET_PX)),
        y: Math.max(0, Math.min(window.innerHeight - 1, y + SPINNER_OFFSET_PX))
    };
}
function updateSpinnerPosition() {
    if (!loadingCursorSpinner || loadingCursorCount === 0) {
        return;
    }
    const { x, y } = getSpinnerPosition();
    loadingCursorSpinner.style.left = `${x}px`;
    loadingCursorSpinner.style.top = `${y}px`;
}
function initializePointerTracking() {
    if (!canUseLoadingCursorDom) {
        return;
    }
    if (pointerTrackingInitialized) {
        return;
    }
    pointerTrackingInitialized = true;
    restoreStoredPointerPosition();
    window.addEventListener('mousemove', (event) => {
        lastPointerX = event.clientX;
        lastPointerY = event.clientY;
        persistPointerPosition();
        updateSpinnerPosition();
    });
}
function getOrCreateLoadingCursorSpinner() {
    if (!canUseLoadingCursorDom) {
        throw new Error('Loading cursor spinner requires browser DOM access.');
    }
    if (loadingCursorSpinner) {
        return loadingCursorSpinner;
    }
    const spinner = document.createElement('div');
    spinner.id = 'loading-cursor-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    spinner.style.position = 'fixed';
    spinner.style.width = '12px';
    spinner.style.height = '12px';
    spinner.style.border = '2px solid var(--text-primary)';
    spinner.style.borderTopColor = 'transparent';
    spinner.style.borderRadius = '50%';
    spinner.style.background = 'transparent';
    spinner.style.pointerEvents = 'none';
    spinner.style.zIndex = '2147483647';
    spinner.style.display = 'none';
    loadingCursorSpinner = spinner;
    return spinner;
}
function showCursorSpinner(body) {
    const spinner = getOrCreateLoadingCursorSpinner();
    if (!spinner.isConnected) {
        body.appendChild(spinner);
    }
    updateSpinnerPosition();
    spinner.style.display = 'block';
    if (typeof spinner.animate !== 'function') {
        return;
    }
    if (!spinnerAnimation) {
        spinnerAnimation = spinner.animate([
            { transform: 'translate(-50%, -50%) rotate(0deg)' },
            { transform: 'translate(-50%, -50%) rotate(360deg)' }
        ], {
            duration: 700,
            iterations: Infinity,
            easing: 'linear'
        });
    }
    else {
        spinnerAnimation.play();
    }
}
function hideCursorSpinner() {
    if (loadingCursorSpinner) {
        loadingCursorSpinner.style.display = 'none';
    }
    if (spinnerAnimation) {
        spinnerAnimation.pause();
    }
}
function applyLoadingCursor() {
    if (!canUseLoadingCursorDom) {
        return;
    }
    const body = document.body;
    if (!body) {
        return;
    }
    showCursorSpinner(body);
}
function clearLoadingCursor() {
    if (!canUseLoadingCursorDom) {
        return;
    }
    hideCursorSpinner();
}
export function beginLoadingCursor() {
    if (!canUseLoadingCursorDom) {
        return;
    }
    loadingCursorCount += 1;
    if (loadingCursorCount === 1) {
        applyLoadingCursor();
    }
}
export function endLoadingCursor() {
    if (!canUseLoadingCursorDom) {
        return;
    }
    if (loadingCursorCount > 0) {
        loadingCursorCount -= 1;
    }
    if (loadingCursorCount === 0) {
        clearLoadingCursor();
    }
}
if (canUseLoadingCursorDom) {
    initializePointerTracking();
}
